/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import useSWR from 'swr';
import qs from 'qs';

import request from '@/utils/request';
import type * as Type from '@/common/interface';

export const useFeaturedVideos = (
  params: Type.VideoFeaturedReq = { page: 1, page_size: 5 },
) => {
  const apiUrl = `/answer/api/v1/videos/featured?${qs.stringify(params)}`;
  const { data, error } = useSWR<Type.ListResult<Type.VideoPublicItem>, Error>(
    apiUrl,
    (url) => request.get(url, { allow404: true }),
  );
  return {
    data,
    isLoading: !data && !error,
    error,
  };
};

export const useVideoUploadConfig = () => {
  const apiUrl = '/answer/api/v1/videos/upload-config';
  const { data, error } = useSWR<Type.VideoUploadConfigResp, Error>(
    apiUrl,
    (url) => request.get(url, { allow404: true }),
  );
  return {
    data,
    isLoading: !data && !error,
    error,
  };
};

export const useVideoReferenceCandidates = (
  params?: Type.VideoReferenceCandidatesReq,
) => {
  const apiUrl = params?.question_id
    ? `/answer/api/v1/videos/reference-candidates?${qs.stringify(params)}`
    : null;
  const { data, error } = useSWR<
    Type.VideoReferenceCandidatesResp,
    Error
  >(apiUrl, (url) => request.get(url, { allow404: true }));
  return {
    data,
    isLoading: Boolean(apiUrl) && !data && !error,
    error,
  };
};

export const useAnswerReferencedVideos = (answerId?: string) => {
  const apiUrl = answerId
    ? `/answer/api/v1/videos/answer-references?${qs.stringify({
        answer_id: answerId,
      })}`
    : null;
  const { data, error } = useSWR<Type.VideoPublicItem[], Error>(
    apiUrl,
    (url) => request.get(url, { allow404: true }),
  );
  return {
    data,
    isLoading: Boolean(apiUrl) && !data && !error,
    error,
  };
};

export const saveAnswerVideoReferences = (
  params: Type.VideoAnswerReferenceSaveReq,
) => {
  return request.post('/answer/api/v1/videos/answer-references', params);
};

export const createVideoPlaybackTicket = (
  params: Type.VideoPlaybackTicketReq,
) => {
  return request.post<Type.VideoPlaybackTicketResp>(
    '/answer/api/v1/videos/playback-ticket',
    params,
  );
};

export const reportVideoPlaybackEvent = (
  params: Type.VideoPlaybackEventReq,
) => {
  return request.post<Type.VideoPlaybackEventResp>(
    '/answer/api/v1/videos/playback-event',
    params,
  );
};

export const createVideoUploadSession = (
  params: Type.VideoUploadSessionReq,
) => {
  return request.post<Type.VideoUploadSessionResp>(
    '/answer/api/v1/videos/upload-session',
    params,
  );
};

export const createVideoDownloadTicket = (
  params: Type.VideoDownloadTicketReq,
) => {
  return request.post<Type.VideoDownloadTicketResp>(
    '/answer/api/v1/videos/download-ticket',
    params,
  );
};

export const unlockVideoMedia = (params: Type.VideoUnlockReq) => {
  return request.post<Type.VideoUnlockResp>(
    '/answer/api/v1/videos/unlock',
    params,
  );
};
